import {HttpInterceptorFn} from '@angular/common/http';
import {environment} from '../../../environments/environment';

/**
 * Rewrites relative API URLs emitted by the orval-generated client
 * (Phase 10 / FE Phase 13) so they hit the configured backend host.
 *
 * The OpenAPI baseline encodes endpoints as `/api/...` (e.g.
 * `/api/templates`), while the hand-written services use
 * `environment.baseApiURL` (`http://host:port/api/`). To keep both worlds
 * working during the migration, this interceptor:
 *
 *   1. Leaves absolute URLs (http://, https://, ws://, wss://) untouched.
 *   2. For relative URLs that start with `/api/` (or `api/`), prepends
 *      `environment.baseApiURL` with its trailing `/api/` segment stripped
 *      so the result becomes `<host>:<port>/api/<rest>`.
 *   3. Leaves all other relative paths (e.g. `/assets/...`) alone.
 *
 * Must run before `loadingInterceptor` so the loading service still sees the
 * rewritten URL with the proper host prefix.
 */
export const baseUrlInterceptor: HttpInterceptorFn = (request, next) => {
    const rawUrl = request.url ?? '';
    if (/^[a-z][a-z0-9+.-]*:/i.test(rawUrl)) {
        return next(request);
    }

    const apiPrefixMatch = /^\/?api\/(.*)$/.exec(rawUrl);
    if (!apiPrefixMatch) {
        return next(request);
    }

    const base = (environment.baseApiURL || '').replace(/\/+$/, '');
    // environment.baseApiURL ends in `/api/`; strip the `/api` so we don't
    // duplicate it when concatenating with the request's `/api/...` path.
    const host = base.endsWith('/api') ? base.slice(0, -'/api'.length) : base;
    const rewrittenUrl = `${host}/api/${apiPrefixMatch[1]}`;

    return next(request.clone({url: rewrittenUrl}));
};
